import { haveValue } from "../../helper/common";

export type SignupEntrySource = "website" | "flatmate-link" | "landlord-link";

const FLATMATE_ENTRY_COOKIE = "flatmateSignupEntry";
const ENTRY_STORAGE_KEY = "signupEntrySource";

function persistEntrySource(source: SignupEntrySource) {
  try {
    sessionStorage.setItem(ENTRY_STORAGE_KEY, source);
  } catch {}
}

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie
    .split(";")
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${name}=`));
  if (!match) return null;
  try {
    return decodeURIComponent(match.split("=").slice(1).join("="));
  } catch {
    return null;
  }
}

function readQueryParam(name: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    return new URLSearchParams(window.location.search).get(name);
  } catch {
    return null;
  }
}

/**
 * Entry source for the end-of-signup joining/sharing flow.
 * - landlord-link: came through the landlord's own link (requestProfileLink present)
 * - flatmate-link: came through a flatmate's link (applications flag preserved via cookie,
 *   or a direct application_id invite link)
 * - website: joined via skortorent.com (default, covers cases 1-4)
 *
 * The detected source is persisted to sessionStorage: landlord-link cookies are
 * consumed (cleared) once the link is registered, and a reload/remount after
 * profile completion must still resolve to landlord-link instead of website.
 * Cleared on every terminal dashboard exit from signup.
 */
export function getSignupEntrySource(): SignupEntrySource {
  const landlordLink =
    readQueryParam("requestProfileLink") ?? readCookie("requestProfileLink");
  if (haveValue(landlordLink)) {
    persistEntrySource("landlord-link");
    return "landlord-link";
  }

  const flatmateFlag =
    readQueryParam("applications") ?? readCookie(FLATMATE_ENTRY_COOKIE);
  if (haveValue(flatmateFlag)) {
    persistEntrySource("flatmate-link");
    return "flatmate-link";
  }

  // Invite links now carry the application id directly
  // (home?applications=true&application_id=...&email=...). Treat a bare
  // application_id as flatmate-link too so future template changes that drop
  // the flag keep working.
  const inviteAppId = getSignupApplicationId();
  if (haveValue(inviteAppId)) {
    persistEntrySource("flatmate-link");
    return "flatmate-link";
  }

  try {
    const stored = sessionStorage.getItem(ENTRY_STORAGE_KEY);
    if (
      stored === "landlord-link" ||
      stored === "flatmate-link" ||
      stored === "website"
    )
      return stored;
  } catch {}
  persistEntrySource("website");
  return "website";
}

/**
 * Application id carried by a flatmate invite link, from the query string or
 * the cookie preserved by ProtectedRoute. Lets the signup flow pre-select the
 * exact pending application instead of guessing the latest one.
 */
export function getSignupApplicationId(): string | null {
  return readQueryParam("application_id") ?? readCookie("application_id");
}

/**
 * Landlord-link params (requestProfileLink=landlordId_workspaceUuid plus
 * optional property_id / invitationToken), from the query string or the
 * cookies preserved by ProtectedRoute. Null once the link is consumed.
 */
export function getLandlordLinkParams(): {
  link: string | null;
  propertyId: string | null;
  invitationToken: string | null;
} {
  return {
    link:
      readQueryParam("requestProfileLink") ?? readCookie("requestProfileLink"),
    propertyId: readQueryParam("property_id") ?? readCookie("property_id"),
    invitationToken:
      readQueryParam("invitationToken") ?? readCookie("invitationToken"),
  };
}

/**
 * Id-based check: is this application already shared with the landlord behind
 * a pending landlord request? Compares raw landlord ids (request landlord_id
 * may be populated or plain). Name-based getSharedLandlordNames is for chips;
 * this is for routing (cases 10/11 auto-share vs choice).
 */
export function isAppSharedWithLandlordId(
  app: any,
  landlordId: string | null | undefined
): boolean {
  if (!landlordId) return false;
  const target = landlordId.toString();
  const shareRequests = Array.isArray(app?.shareRequests)
    ? app.shareRequests
    : [];
  for (const req of shareRequests) {
    const id = req?.landlord_id?._id ?? req?.landlord_id;
    if (id && id.toString() === target) return true;
  }
  const sharedProfiles = Array.isArray(app?.sharedProfiles)
    ? app.sharedProfiles
    : [];
  for (const sp of sharedProfiles) {
    const id = sp?.id?.landlordid?._id ?? sp?.id?.landlordid;
    if (id && id.toString() === target) return true;
  }
  return false;
}

export function getSharedLandlordNames(app: any): string[] {
  const names = new Map<string, string>();
  const sharedProfiles = Array.isArray(app?.sharedProfiles)
    ? app.sharedProfiles
    : [];
  for (const sp of sharedProfiles) {
    const landlord = sp?.id?.landlordid;
    const key =
      landlord?._id?.toString?.() ?? landlord?.email?.toLowerCase?.() ?? null;
    const label =
      `${landlord?.fname_companyName ?? ""} ${landlord?.lname ?? ""}`.trim() ||
      landlord?.email ||
      "";
    if (key && label && !names.has(key)) names.set(key, label);
  }
  const shareRequests = Array.isArray(app?.shareRequests)
    ? app.shareRequests
    : [];
  for (const req of shareRequests) {
    const landlord = req?.landlord_id;
    const key =
      landlord?._id?.toString?.() ?? landlord?.email?.toLowerCase?.() ?? null;
    const label =
      `${landlord?.fname_companyName ?? ""} ${landlord?.lname ?? ""}`.trim() ||
      landlord?.email ||
      "";
    if (key && label && !names.has(key)) names.set(key, label);
  }
  return Array.from(names.values());
}

export const FLATMATE_ENTRY_COOKIE_NAME = FLATMATE_ENTRY_COOKIE;

/** Clear persisted signup state before a terminal dashboard navigation or logout. */
export function clearSignupPersistedState(): void {
  for (const key of [
    "signupJoinedResult",
    "signupSharedResult",
    "signupLandlordQueueState",
    "signupGuarantorBacking",
    ENTRY_STORAGE_KEY,
  ]) {
    try {
      sessionStorage.removeItem(key);
    } catch {}
  }
  for (const name of [
    FLATMATE_ENTRY_COOKIE,
    "application_id",
    "invitedEmail",
    "tenantSignupFlow",
    "requestProfileLink",
    "property_id",
    "invitationToken",
    "sharedProfileLink",
  ]) {
    try {
      document.cookie = `${name}=; path=/; max-age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    } catch {}
  }
}
